import { Button } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import { BATCH_STATUSES, CREATION_METHODS } from '../constants';
import { ALL, BatchFilters } from '../utils/batchFilterUtils';

interface BatchFilterBarProps {
    filters: BatchFilters;
    onChange: (filters: BatchFilters) => void;
    onFilter: () => void;
    onReset: () => void;
    creationSites: string[];
    destinations: string[];
}

export const BatchFilterBar: React.FC<BatchFilterBarProps> = ({ filters, onChange, onFilter, onReset, creationSites, destinations }) => {
    const { t } = useTranslation();
    const all = { value: ALL, label: String(t('wms.sortingBatch.filters.all')) };
    const labels = {
        batchNumber: String(t('wms.sortingBatch.filters.batchNumber')),
        creationSite: String(t('wms.sortingBatch.filters.creationSite')),
        createdTime: String(t('wms.sortingBatch.filters.createdTime')),
        destination: String(t('wms.sortingBatch.filters.destination')),
        status: String(t('wms.sortingBatch.filters.status')),
        creationMethod: String(t('wms.sortingBatch.filters.creationMethod')),
    };
    const col1 = [labels.batchNumber, labels.destination];
    const col2 = [labels.creationSite, labels.status];
    const col3 = [labels.createdTime, labels.creationMethod];

    const statusOptions = useMemo(() => [
        { value: ALL, label: String(t('wms.sortingBatch.filters.all')) },
        ...BATCH_STATUSES.map((s) => ({ value: s, label: String(t(`wms.sortingBatch.status.${s}`)) })),
    ], [t]);
    const methodOptions = useMemo(() => [
        { value: ALL, label: String(t('wms.sortingBatch.filters.all')) },
        ...CREATION_METHODS.map((m) => ({ value: m, label: String(t(`wms.sortingBatch.creationMethodFull.${m}`)) })),
    ], [t]);

    return (
        <div className="mb-4">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <InputFilterItem
                        label={labels.batchNumber}
                        value={filters.batchNumber}
                        onChange={(e) => onChange({ ...filters, batchNumber: e.target.value })}
                        placeholder={String(t('wms.sortingBatch.filters.typeIn'))}
                        spanClassName={getSmartLabelClassName(col1)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labels.creationSite}
                        options={[all, ...creationSites.map((s) => ({ value: s, label: s }))]}
                        value={filters.creationSite}
                        onChange={(v: string) => onChange({ ...filters, creationSite: v || ALL })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col2)}
                    />
                </div>
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={labels.createdTime}
                        value={filters.createdRange}
                        onChange={(range) => onChange({ ...filters, createdRange: range })}
                        spanClassName={getSmartLabelClassName(col3)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labels.destination}
                        options={[all, ...destinations.map((d) => ({ value: d, label: d }))]}
                        value={filters.destination}
                        onChange={(v: string) => onChange({ ...filters, destination: v || ALL })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col1)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labels.status}
                        options={statusOptions}
                        value={filters.status}
                        onChange={(v: BatchFilters['status']) => onChange({ ...filters, status: v || ALL })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col2)}
                        showSearch={false}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labels.creationMethod}
                        options={methodOptions}
                        value={filters.creationMethod}
                        onChange={(v: BatchFilters['creationMethod']) => onChange({ ...filters, creationMethod: v || ALL })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col3)}
                        showSearch={false}
                    />
                </div>
            </div>
            <div className="flex justify-end items-center mt-3 gap-2">
                <Button type="primary" onClick={onFilter}>{t('wms.sortingBatch.filters.filter')}</Button>
                <Button onClick={onReset}>{t('wms.sortingBatch.filters.reset')}</Button>
            </div>
        </div>
    );
};
